import React from 'react'
import ContextMenu from '../ui/ContextMenu'
import { useTable, usePagination, Column, useSortBy } from 'react-table'
import { IPromotion, IPromotionFilter } from '../../types'
import ReactTablePagination from '../ui/ReactTablePagination'
import { RouteComponentProps, Link } from 'react-router-dom'
import classNames from 'classnames'
import moment from 'moment'
export interface ITablePromotionsProps extends RouteComponentProps<any> {
  ownProps: RouteComponentProps<any>
  orgId: string
  isActive: boolean
  filters: IPromotionFilter
  promotions: IPromotion[]
  promotionCount: number
  getAllPromotions: (params: IPromotionFilter) => Promise<IPromotion[]>
  deletePromotion: (promotionId: string) => Promise<any>
  updatePromotionFilters: (params: IPromotionFilter) => Promise<any>
  archivePromotion: (promotionId: string) => void
}

export interface ITableDriversState {
  isArchived: boolean
  search: string
  pages: number
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  // filters: IPromotion
  data: T[]
  loading: boolean
  pageCount: number
  pageSize: number
  isActive: boolean
  fetchData: (params: { pageIndex: number; pageSize: number; active: boolean }) => void
  updateFilters: (params: IPromotionFilter) => Promise<any>
}

const PAGE_SIZE = 10

function Table<T extends object>({
  columns,
  data,
  fetchData,
  updateFilters,
  // filters,
  pageSize,
  loading,
  isActive,
  pageCount: controlledPageCount
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { pageIndex, sortBy } = state

  React.useEffect(() => {
    fetchData({ pageIndex, pageSize, active: isActive })
  }, [pageIndex, pageSize, isActive])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'DESC' : 'ASC')
    })
    updateFilters({ sortBy: sortParams })
  }, [sortBy])

  return (
    <React.Fragment>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td>Loading...</td>
            </tr>
          ) : (
            rows.map(
              (row, i) =>
                +prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, index) => {
                      return (
                        <td {...cell.getCellProps()} key={`1-${index}`}>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
            )
          )}
        </tbody>
      </table>
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}
const renderCreatedDate = (promotion: IPromotion) => {
  if (!promotion || !promotion.createdAt) {
    return ''
  }
  return moment(promotion.createdAt).format('MMM DD YYYY')
}
export default function TablePromotions(props: ITablePromotionsProps) {
  const [loading, setLoading] = React.useState(false)
  const [pageCount, setPageCount] = React.useState(0)
  const [pageSize] = React.useState(PAGE_SIZE)
  const columns = React.useMemo(
    () => [
      {
        id: 'createdAt',
        Header: 'Created Date',
        accessor: (promotion: IPromotion) => renderCreatedDate(promotion),
        className: 'flex items-center'
      },
      {
        id: 'title',
        Header: 'Title',
        accessor: (promotion: IPromotion) => {
          return <Link to={`/promotions/riders/${promotion.id}`}>{promotion.title}</Link>
        },
        width: 200
      },
      {
        id: 'description',
        Header: 'Description',
        disableSortBy: true,
        accessor: (promotion: IPromotion) => {
          return <div>{promotion.note}</div>
        },
        className: 'flex items-center'
      },
      {
        id: 'maxRedemptions',
        Header: 'Max Redemption Count',
        accessor: 'maxRedemptions',
        className: 'flex items-center'
      },
      {
        id: 'buttons',
        Header: '',
        accessor: (promotion: IPromotion) => {
          if (props.isActive === true) {
            return <ButtonsMenus promotionId={promotion.id} />
          }
          return <div />
        }
      }
    ],
    [props.isActive]
  )
  React.useEffect(() => {
    props.updatePromotionFilters({
      sortBy: ['createdAt-DESC']
    })
  }, [])
  React.useEffect(() => {
    setPageCount(Math.ceil(props.promotionCount / pageSize))
  }, [props.promotionCount])
  React.useEffect(() => {
    fetchData({ pageIndex: 0, pageSize: PAGE_SIZE, active: props.isActive })
  }, [props.orgId, props.filters, props.isActive])
  const ButtonsMenus = (props: { promotionId: string }) => {
    return (
      <ContextMenu>
        <div className='onDeactivateOption' onClick={() => deletePromotion(props.promotionId)}>
          <span className='font-dark-grey'>Delete</span>
        </div>
        <div className='onDeactivateOption' onClick={() => archivePromotion(props.promotionId)}>
          <span className='font-dark-grey'>Archive</span>
        </div>
      </ContextMenu>
    )
  }

  const deletePromotion = (id: string) => {
    props.deletePromotion(id)
  }
  const archivePromotion = (promotionId: string) => {
    props.archivePromotion(promotionId)
  }
  const setTableLoading = () => {
    if (!props.promotions) {
      setLoading(true)
    } else if (props.promotions) {
      setLoading(false)
    }
  }
  const fetchData = React.useCallback(async ({ pageSize, pageIndex, active }) => {
    setTableLoading()
    if (props.filters.orgId) {
      props.getAllPromotions({ ...props.filters, skip: pageIndex * PAGE_SIZE, take: pageSize, active })
    }
  }, [props.orgId, props.filters, props.isActive])
  return (
    <React.Fragment>
      <div className='react-table'>
        <Table
          columns={columns}
          data={props.promotions}
          fetchData={fetchData}
          loading={loading}
          // filters={props.filters}
          pageCount={pageCount}
          pageSize={pageSize}
          updateFilters={props.updatePromotionFilters}
          isActive={props.isActive}
        />
      </div>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/promotions/PromotionsTable.tsx